import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
} from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

import DataTable from "react-data-table-component";

import useProperty from "../../queries/property/getProperties";

import Loader from "../../components/Loader";
import Toast from "../../utils/Toast";
import Swal from "sweetalert2";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import useDeleteProperty from "../../mutation/property/deleteProperty";
import { AuthContext } from "../../utils/userProvider";
import useCreateAudit from "../../mutation/audit/createAudit";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
});

const Property = () => {
  const [propertyTable, setPropertyTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);

  const [propertyResponse, resetProperty] = useProperty();

  let columns = [
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "RMS ID",
      selector: "rmsId",
      sortable: false,
    },
    {
      name: "Property(s)",
      selector: "suiteIds",
      sortable: false,
      cell: (row, index, column, id) => {
        let output = "";
        if (row.suiteIds && row.suiteIds.ids && propertyResponse.data) {
          let properties = propertyResponse.data;
          let suiteIds = row.suiteIds.ids;
          for (var i = 0; i < suiteIds.length; i++) {
            if (i > 0) output += ", ";
            let suiteId = typeof suiteIds[i] === "number" ? suiteIds[i] : parseInt(suiteIds[i], 10);
            let prop = properties.find(
              (p) => p.suiteIds?.ids && Array.isArray(p.suiteIds.ids) && p.suiteIds.ids.includes(suiteId)
            );
            output += prop ? `${prop.name} (Suite ID: ${suiteId})` : `Suite ID: ${suiteId}`;
          }
        }

        return <span>{output}</span>;
      },
    },
  ];

  columns.push({
    name: "Options",
    sortable: false,
    cell: (row, index, column, id) => {
      if (authState.editAccess) {
        if (!row.archived) {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CreateIcon />}
                onClick={() => {
                  editItem(row);
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  deleteItem(row);
                }}
              >
                Delete
              </Button>
            </div>
          );
        } else {
          return null;
        }
      } else {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                editItem(row);
              }}
            >
              View
            </Button>
          </div>
        );
      }
    },
  });

  const editItem = (value) => {
    if (authState.role == "Executive") {
      window.location.href = "/dashboard/property/edit/" + value.id;
    } else {
      window.location.href = "/dashboard/property/view/" + value.id;
    }
  };

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = propertyTable.filter(
    (item) =>
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.suiteText &&
        item.suiteText.toLowerCase().includes(filterText.toLowerCase()))
  );

  const newProperty = () => {
    window.location.href = "/dashboard/property/new";
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  useEffect(() => {
    if (propertyResponse.data) {
      let properties = propertyResponse.data;

      for (var i = 0; i < properties.length; i++) {
        let propertyText = "";
        if (properties[i].suiteIds && properties[i].suiteIds.ids) {
          let suiteIds = properties[i].suiteIds.ids;
          for (var a = 0; a < suiteIds.length; a++) {
            let suiteId = typeof suiteIds[a] === "number" ? suiteIds[a] : parseInt(suiteIds[a], 10);
            let prop = properties.find(
              (p) => p.suiteIds?.ids && Array.isArray(p.suiteIds.ids) && p.suiteIds.ids.includes(suiteId)
            );
            if (prop) {
              propertyText += prop.name + ` (Suite ID: ${suiteId})` + ", ";
            } else {
              propertyText += `Suite ID: ${suiteId}` + ", ";
            }
          }
        }
        if (propertyText.length > 0) {
          propertyText = propertyText.slice(0, -2);
        }
        properties[i].suiteText = propertyText;
      }

      setPropertyTable(properties);
    }
  }, [propertyResponse]);

  const [openDeleteProperty, setOpenDeleteProperty] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [auditResponse, setAuditData] = useCreateAudit();

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeleteProperty(true);
  };

  const handleClose = () => {
    setOpenDeleteProperty(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "props",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Property deleted",
      });
      setDeleteRow(null);
      setOpenDeleteProperty(false);
      resetProperty();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeleteProperty(false);
    }
  }, [auditResponse]);

  return (
    <ListPageShell>
      <ListPageCard>
        {propertyResponse.loading && <Loader />}
        {!propertyResponse.loading && (
          <div>
            <ListPageHeader
              title="Property"
              actions={
                authState.role == "Executive" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={newProperty}
                  >
                    Create New Property
                  </Button>
                ) : null
              }
            />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10]}
              noHeader={true}
              subHeader
              subHeaderComponent={subHeaderComponentMemo}
              data={filteredItems}
              columns={columns}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        sx={modalSx}
        open={openDeleteProperty}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && auditResponse.loading) return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus={true}
      >
        <Fade in={openDeleteProperty}>
          <Box>
            <DeleteModalFrame title="Delete Property">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="inherit"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default Property;
